import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

/**
 * Members UI Store - Manages UI state for members feature
 * Uses Composition API pattern with computed properties
 */
export const useMembersUIStore = defineStore('members-ui', () => {
  // ===== STATE =====

  // Filter state
  const searchQuery = ref('')
  const roleFilter = ref('all')
  const memberStatus = ref<string | undefined>(undefined)

  // Pagination state
  const currentPage = ref(1)
  const pageSize = ref(20)

  // Modal state
  const showMembershipAnswersModal = ref(false)
  const selectedMemberForReview = ref<Record<string, unknown> | null>(null)
  const showWithdrawInvitationModal = ref(false)
  const selectedMemberForWithdraw = ref<Record<string, unknown> | null>(null)

  // Loading state
  const isLoadingFilteredMembers = ref(false)

  // ===== COMPUTED =====

  const hasActiveFilters = computed(() => {
    return searchQuery.value !== '' || roleFilter.value !== 'all' || memberStatus.value !== undefined
  })

  // ===== ACTIONS =====

  // Filter actions
  const setSearchQuery = (query: string) => {
    searchQuery.value = query
    currentPage.value = 1 // Reset pagination when filter changes
  }

  const setRoleFilter = (role: string) => {
    roleFilter.value = role
    currentPage.value = 1 // Reset pagination when filter changes
  }

  const setMemberStatus = (status: string | undefined) => {
    memberStatus.value = status
    currentPage.value = 1 // Reset pagination when filter changes
  }

  // Pagination actions
  const setCurrentPage = (page: number) => {
    currentPage.value = page
  }

  const setPageSize = (size: number) => {
    pageSize.value = size
    currentPage.value = 1 // Reset to first page when page size changes
  }

  // Modal actions
  const openMembershipAnswersModal = (member: Record<string, unknown>) => {
    selectedMemberForReview.value = member
    showMembershipAnswersModal.value = true
  }

  const closeMembershipAnswersModal = () => {
    showMembershipAnswersModal.value = false
    selectedMemberForReview.value = null
  }

  const openWithdrawInvitationModal = (member: Record<string, unknown>) => {
    selectedMemberForWithdraw.value = member
    showWithdrawInvitationModal.value = true
  }

  const closeWithdrawInvitationModal = () => {
    showWithdrawInvitationModal.value = false
    selectedMemberForWithdraw.value = null
  }

  const setLoadingFilteredMembers = (loading: boolean) => {
    isLoadingFilteredMembers.value = loading
  }

  // Reset all filters and state
  const resetFilters = () => {
    searchQuery.value = ''
    roleFilter.value = 'all'
    memberStatus.value = undefined
    currentPage.value = 1
  }

  const resetState = () => {
    resetFilters()
    showMembershipAnswersModal.value = false
    selectedMemberForReview.value = null
    showWithdrawInvitationModal.value = false
    selectedMemberForWithdraw.value = null
    isLoadingFilteredMembers.value = false
  }

  return {
    // State (as computed for reactivity)
    searchQuery: computed(() => searchQuery.value),
    roleFilter: computed(() => roleFilter.value),
    memberStatus: computed(() => memberStatus.value),
    currentPage: computed(() => currentPage.value),
    pageSize: computed(() => pageSize.value),
    showMembershipAnswersModal: computed(() => showMembershipAnswersModal.value),
    selectedMemberForReview: computed(() => selectedMemberForReview.value),
    showWithdrawInvitationModal: computed(() => showWithdrawInvitationModal.value),
    selectedMemberForWithdraw: computed(() => selectedMemberForWithdraw.value),
    isLoadingFilteredMembers: computed(() => isLoadingFilteredMembers.value),

    // Computed
    hasActiveFilters,

    // Actions
    setSearchQuery,
    setRoleFilter,
    setMemberStatus,
    setCurrentPage,
    setPageSize,
    openMembershipAnswersModal,
    closeMembershipAnswersModal,
    openWithdrawInvitationModal,
    closeWithdrawInvitationModal,
    setLoadingFilteredMembers,
    resetFilters,
    resetState,
  }
})
